export const FileType = {
  ATOM: 'application/atom+xml',
  BMP: 'image/bmp',
  CSS: 'text/css',
  CSV: 'text/csv',
  DOC: 'application/msword',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  EPUB: 'application/epub+zip',
  GIF: 'image/gif',
  HTML: 'text/html',
  MHTML: 'multipart/related',
  JPEG: 'image/jpeg',
  JS: 'application/javascript',
  JSON: 'application/json',
  MD: 'text/markdown',
  ODP: 'application/vnd.oasis.opendocument.presentation',
  ODS: 'application/vnd.oasis.opendocument.spreadsheet',
  ODT: 'application/vnd.oasis.opendocument.text',
  PDF: 'application/pdf',
  PNG: 'image/png',
  PPT: 'application/vnd.ms-powerpoint',
  PPTX: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  RSS: 'application/rss+xml',
  RTF: 'text/rtf',
  SVG: 'image/svg+xml',
  TIFF: 'image/tiff',
  TSV: 'text/tab-separated-values',
  TXT: 'text/plain',
  WEBP: 'image/webp',
  XLS: 'application/vnd.ms-excel',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  XML: 'application/xml',
  ZIP: 'application/zip',
} as const;

export type FileType = (typeof FileType)[keyof typeof FileType];

export const FileExtension = {
  [FileType.ATOM]: 'atom',
  [FileType.BMP]: 'bmp',
  [FileType.CSS]: 'css',
  [FileType.CSV]: 'csv',
  [FileType.DOCX]: 'docx',
  [FileType.DOC]: 'doc',
  [FileType.EPUB]: 'epub',
  [FileType.GIF]: 'gif',
  [FileType.HTML]: 'html',
  [FileType.JPEG]: 'jpg',
  [FileType.JSON]: 'json',
  [FileType.JS]: 'js',
  [FileType.MD]: 'md',
  [FileType.MHTML]: 'mhtml',
  [FileType.ODP]: 'odp',
  [FileType.ODS]: 'ods',
  [FileType.ODT]: 'odt',
  [FileType.PDF]: 'pdf',
  [FileType.PNG]: 'png',
  [FileType.PPT]: 'ppt',
  [FileType.PPTX]: 'pptx',
  [FileType.RSS]: 'rss',
  [FileType.RTF]: 'rtf',
  [FileType.SVG]: 'svg',
  [FileType.TIFF]: 'tiff',
  [FileType.TSV]: 'tsv',
  [FileType.TXT]: 'txt',
  [FileType.WEBP]: 'webp',
  [FileType.XLSX]: 'xlsx',
  [FileType.XLS]: 'xls',
  [FileType.XML]: 'xml',
  [FileType.ZIP]: 'zip',
} as const;

export type FileExtension = (typeof FileExtension)[keyof typeof FileExtension];

export const FileTypeLabel = {
  [FileType.ATOM]: 'Atom Feed',
  [FileType.BMP]: 'BMP',
  [FileType.CSS]: 'CSS',
  [FileType.CSV]: 'CSV',
  [FileType.DOCX]: 'Word',
  [FileType.DOC]: 'Word',
  [FileType.EPUB]: 'EPUB',
  [FileType.GIF]: 'GIF',
  [FileType.HTML]: 'HTML',
  [FileType.JPEG]: 'JPEG',
  [FileType.JSON]: 'JSON',
  [FileType.JS]: 'JavaScript',
  [FileType.MD]: 'Markdown',
  [FileType.MHTML]: 'MHTML',
  [FileType.ODP]: 'OpenDocument Presentation',
  [FileType.ODS]: 'OpenDocument Spreadsheet',
  [FileType.ODT]: 'OpenDocument Text',
  [FileType.PDF]: 'PDF',
  [FileType.PNG]: 'PNG',
  [FileType.PPT]: 'PowerPoint',
  [FileType.PPTX]: 'PowerPoint',
  [FileType.RSS]: 'RSS Feed',
  [FileType.RTF]: 'Rich Text',
  [FileType.SVG]: 'SVG',
  [FileType.TIFF]: 'TIFF',
  [FileType.TSV]: 'TSV',
  [FileType.TXT]: 'Text',
  [FileType.WEBP]: 'WebP',
  [FileType.XLSX]: 'Excel',
  [FileType.XLS]: 'Excel',
  [FileType.XML]: 'XML',
  [FileType.ZIP]: 'ZIP Archive',
} as const;

export type FileTypeLabel = (typeof FileTypeLabel)[keyof typeof FileTypeLabel];

/**
 * All supported file types as an array for validation.
 */
export const ALL_FILE_TYPES = Object.values(FileType);

/**
 * All file extensions as a comma-separated string for HTML input accept attribute.
 */
export const ALL_FILE_EXTENSIONS_STRING = Object.values(FileExtension)
  .map((ext) => `.${ext}`)
  .join(',');

/**
 * Gets the file extension(s) for a given file type.
 * Returns the file extension(s) corresponding to the file type.
 * For types with multiple extensions, returns an array.
 */
export function getFileTypeExtension(
  fileType: FileType,
): string | readonly string[] {
  return FileExtension[fileType];
}

/**
 * Gets a user-friendly file type label from MIME type.
 */
export function getFileTypeLabel(
  fileType: FileType,
): string | readonly string[] {
  return FileTypeLabel[fileType];
}

/**
 * Checks if a MIME type is an image type.
 */
export function isImageMimeType(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/**
 * Checks if a file extension corresponds to an image type.
 */
export function isImageExtension(extension: string): boolean {
  const normalizedExtension = extension.toLowerCase();

  // Get image extensions from FileType mapping
  const imageFileTypes = Object.values(FileType).filter((type) =>
    type.startsWith('image/'),
  );
  const imageExtensions = imageFileTypes.flatMap(getFileTypeExtension);

  return imageExtensions.includes(normalizedExtension);
}

/**
 * Checks if a file is an image type.
 */
export function isImageFile(file: File): boolean {
  // Check MIME type first
  if (isImageMimeType(file.type)) {
    return true;
  }

  // Fallback to extension check for files with incorrect/missing MIME types
  const extension = file.name.split('.').pop();

  if (!extension) {
    return false;
  }

  return isImageExtension(extension);
}
